import { Context } from '../context/Context';
import { FullscreenQuad } from './FullscreenQuad';
import { RenderTexture, createColorTexture, createFramebuffer } from './RenderTarget';

const MIP_CHAIN_LENGTH = 5;

type MipLevel = {
  width: number;
  height: number;
  texture: RenderTexture;
  fbo: WebGLFramebuffer;
};

/**
 * Additive-glow bloom. Reads a caller-owned HDR scene texture, downsamples to
 * a mip chain, upsamples back, then **additively blends** the resulting glow
 * into the same HDR scene FBO the caller passes in. No tonemapping — that
 * happens in {@link FinalPresentPass}, which always runs.
 */
export class BloomPass {
  private mipChain: MipLevel[] = [];
  private width!: number;
  private height!: number;

  // Tunable parameters
  public threshold = 1.0;
  public strength = 0.04;

  init(ctx: Context): void {
    const gl = ctx.gl;
    this.width = ctx.viewPort.width;
    this.height = ctx.viewPort.height;

    let w = Math.max(1, this.width >> 1);
    let h = Math.max(1, this.height >> 1);
    this.mipChain = [];

    for (let i = 0; i < MIP_CHAIN_LENGTH; i++) {
      const tex = new RenderTexture();
      tex.glTexture = createColorTexture(gl, w, h, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT);
      const fbo = createFramebuffer(gl, tex.glTexture!);
      this.mipChain.push({ width: w, height: h, texture: tex, fbo });
      w = Math.max(1, w >> 1);
      h = Math.max(1, h >> 1);
    }
  }

  /**
   * Downsample + upsample the input HDR scene, then additively blend the
   * resulting glow back into {@link outputBuffer} (typically the same HDR
   * scene FBO the scene was rendered into).
   */
  render(
    ctx: Context,
    quad: FullscreenQuad,
    inputScene: RenderTexture,
    outputBuffer: WebGLFramebuffer | null,
  ): void {
    const gl = ctx.gl;

    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);

    // === Downsample chain ===
    // First pass: threshold extraction + downsample from the scene texture.
    // Subsequent passes: plain downsample from previous mip.
    const downShader = ctx.engine.shaderManager.getBloomShader('DOWNSAMPLE');

    for (let i = 0; i < this.mipChain.length; i++) {
      const mip = this.mipChain[i];
      gl.bindFramebuffer(gl.FRAMEBUFFER, mip.fbo);
      gl.viewport(0, 0, mip.width, mip.height);

      ctx.setShader(downShader);

      const source = i === 0 ? inputScene : this.mipChain[i - 1].texture;
      const srcW = i === 0 ? this.width : this.mipChain[i - 1].width;
      const srcH = i === 0 ? this.height : this.mipChain[i - 1].height;

      downShader.uniformsContainer
        .setInputTexture(source)
        .setTexelSize([1.0 / srcW, 1.0 / srcH])
        .setThreshold(i === 0 ? this.threshold : 0.0);

      downShader.validate();
      quad.draw(gl);
    }

    // === Upsample chain ===
    // Progressively upsample from smallest mip back to largest, additively
    // blending into each level.
    const upShader = ctx.engine.shaderManager.getBloomShader('UPSAMPLE');

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);

    for (let i = this.mipChain.length - 2; i >= 0; i--) {
      const target = this.mipChain[i];
      const source = this.mipChain[i + 1];

      gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
      gl.viewport(0, 0, target.width, target.height);

      ctx.setShader(upShader);

      upShader.uniformsContainer
        .setInputTexture(source.texture)
        .setTexelSize([1.0 / source.width, 1.0 / source.height])
        .setBloomIntensity(1.0);

      upShader.validate();
      quad.draw(gl);
    }

    // === Final additive composite into the HDR scene FBO ===
    // Blend stays enabled with ONE/ONE so glow * strength is summed onto the
    // existing scene radiance. FinalPresentPass tonemaps the result later.
    ctx.textureState.clear();
    gl.bindFramebuffer(gl.FRAMEBUFFER, outputBuffer);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);

    const compShader = ctx.engine.shaderManager.getBloomShader('COMPOSITE');
    ctx.setShader(compShader);

    compShader.uniformsContainer
      .setInputTexture(this.mipChain[0].texture)
      .setBloomStrength(this.strength);

    compShader.validate();
    quad.draw(gl);

    gl.disable(gl.BLEND);
  }

  destroy(gl: WebGL2RenderingContext): void {
    for (const mip of this.mipChain) {
      gl.deleteFramebuffer(mip.fbo);
      if (mip.texture.glTexture) gl.deleteTexture(mip.texture.glTexture);
    }
    this.mipChain = [];
  }
}
